import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    IconButton,
    Stack,
    CircularProgress,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useGetApiQuery, useSubmitDataMutation } from '../../../services/goroskop';
import { getZodiacSignFromDate, getZodiacTitle, normalizeZodiacKey } from '../../../utils/zodiac';
import { getEasternSignByYear, getEasternTitle, normalizeEasternKey } from '../../../utils/easternZodiac';

import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ru';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ruRU } from '@mui/x-date-pickers/locales';
import '../../../datepicker.css';

dayjs.locale('ru');
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.tz.setDefault('Europe/Moscow');

const DEFAULT_CONFIG = {
    intro: 'Вам успешно подобраны люди, удовлетворяющие вашим условиям поиска по возрасту.',
    incompatible: 'К сожалению Ваш бывший партнер с вами был несовместим.',
    astrologer: 'За подробностями обратитесь к Свату-Астрологу Вячеславу Горелову',
    check_title: 'Экспресс-проверка совместимости',
    check_subtitle: 'Вы можете прямо сейчас проверить любого бывшего партнера на несовместимость:'
};

const getCategoryTheme = (cat: 'zodiac' | 'eastern' | 'number') => {
    switch (cat) {
        case 'zodiac':
            return {
                bg: 'linear-gradient(135deg, #f4f8fe 0%, #e8f1fc 100%)',
                border: '1px solid #bbdefb',
                borderLeft: '4px solid #1976d2',
                titleColor: '#0d47a1',
                chipColor: '#1565c0',
                chipBorder: '#90caf9',
                chipBg: '#ffffff',
                icon: '✨'
            };
        case 'eastern':
            return {
                bg: 'linear-gradient(135deg, #fffbf5 0%, #fff4e5 100%)',
                border: '1px solid #ffe0b2',
                borderLeft: '4px solid #f57c00',
                titleColor: '#bf360c',
                chipColor: '#d84315',
                chipBorder: '#ffcc80',
                chipBg: '#ffffff',
                icon: '🐉'
            };
        case 'number':
        default:
            return {
                bg: 'linear-gradient(135deg, #f2fcf9 0%, #e0f4f1 100%)',
                border: '1px solid #b2dfdb',
                borderLeft: '4px solid #00897b',
                titleColor: '#004d40',
                chipColor: '#00695c',
                chipBorder: '#80cbc4',
                chipBg: '#ffffff',
                icon: '🔢'
            };
    }
};

interface MatchBannerDialogProps {
    open: boolean;
    onClose: () => void;
    totalCount: number;
    mainCount?: number;
    vkCount?: number;
    isLoading?: boolean;
    userAccount?: any;
}

export const MatchBannerDialog: React.FC<MatchBannerDialogProps> = ({
    open,
    onClose,
    totalCount,
    mainCount = 0,
    vkCount = 0,
    isLoading = false,
    userAccount
}) => {
    const { data: modalConfigData } = useGetApiQuery('welcome_modal.php');
    const [fetchProfile, profileQuery] = useSubmitDataMutation();

    useEffect(() => {
        if (!userAccount || Object.keys(userAccount).length === 0) {
            const fd = new FormData();
            fetchProfile({ name: 'profile.php', data: fd });
        }
    }, [userAccount, fetchProfile]);

    const activeUser = (userAccount && Object.keys(userAccount).length > 0)
        ? userAccount
        : (profileQuery.data?.[0] || (() => {
            try {
                const stored = localStorage.getItem('user_account');
                return stored ? JSON.parse(stored) : {};
            } catch (e) {
                return {};
            }
        })());

    const introText = (modalConfigData as any)?.[0]?.intro || DEFAULT_CONFIG.intro;
    const incompatibleText = (modalConfigData as any)?.[0]?.incompatible || DEFAULT_CONFIG.incompatible;
    const astrologerText = (modalConfigData as any)?.[0]?.astrologer || DEFAULT_CONFIG.astrologer;
    const checkTitleText = (modalConfigData as any)?.[0]?.check_title || DEFAULT_CONFIG.check_title;
    const checkSubtitleText = (modalConfigData as any)?.[0]?.check_subtitle || DEFAULT_CONFIG.check_subtitle;

    const [partnerDate, setPartnerDate] = useState<Dayjs | null>(null);
    const [validationError, setValidationError] = useState<string>('');
    const [isChecking, setIsChecking] = useState<boolean>(false);
    const [checkResult, setCheckResult] = useState<boolean>(false);
    const [matchedReasons, setMatchedReasons] = useState<Array<{
        category: 'zodiac' | 'eastern' | 'number';
        title: string;
        pairLabel: string;
        reasonText: string;
    }>>([]);

    const handleCheckCompatibility = (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError('');
        setCheckResult(false);
        setMatchedReasons([]);

        if (!partnerDate || !partnerDate.isValid()) {
            setValidationError('Пожалуйста, укажите дату рождения партнера');
            return;
        }

        const today = dayjs();
        if (partnerDate.isAfter(today)) {
            setValidationError('Дата рождения не может быть в будущем');
            return;
        }

        // Вычисляем полный возраст
        const age = today.diff(partnerDate, 'year');

        if (age < 18) {
            setValidationError('Возраст партнера должен быть не менее 18 лет');
            return;
        }

        // Извлекаем сохраненные матрицы
        const zodiacPairs = (modalConfigData as any)?.[0]?.zodiac_pairs || {};
        let easternPairs = (modalConfigData as any)?.[0]?.eastern_pairs;
        if (!easternPairs || typeof easternPairs !== 'object') {
            try {
                const c = localStorage.getItem('astro_eastern_pairs');
                easternPairs = c ? JSON.parse(c) : {};
            } catch (e) { easternPairs = {}; }
        }
        let numberPairs = (modalConfigData as any)?.[0]?.number_pairs;
        if (!numberPairs || typeof numberPairs !== 'object') {
            try {
                const c = localStorage.getItem('astro_number_pairs');
                numberPairs = c ? JSON.parse(c) : {};
            } catch (e) { numberPairs = {}; }
        }

        // Данные партнера
        const pDay = partnerDate.date();
        const pMonth = partnerDate.month() + 1;
        const pYear = partnerDate.year();
        const pZodiac = getZodiacSignFromDate(pDay, pMonth);
        const pEastern = getEasternSignByYear(pYear)?.key;

        // Данные пользователя
        const currentUser = activeUser;
        const userBdayStr: string = currentUser?.birthday || currentUser?.birthd || '';
        let uDay: number | null = null;
        let uMonth: number | null = null;
        let uYear: number | null = null;
        if (userBdayStr) {
            const mIso = userBdayStr.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})/);
            if (mIso) {
                uYear = parseInt(mIso[1], 10);
                uMonth = parseInt(mIso[2], 10);
                uDay = parseInt(mIso[3], 10);
            } else {
                const mRu = userBdayStr.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
                if (mRu) {
                    uDay = parseInt(mRu[1], 10);
                    uMonth = parseInt(mRu[2], 10);
                    uYear = parseInt(mRu[3], 10);
                }
            }
        }

        const profileZodiac = currentUser?.zodiak ? normalizeZodiacKey(currentUser.zodiak) : '';
        const calcZodiac = (uDay && uMonth) ? getZodiacSignFromDate(uDay, uMonth) : '';
        const userZodiacCandidates = Array.from(new Set([profileZodiac, calcZodiac].filter(Boolean)));

        const profileEastern = (currentUser?.birthyear || currentUser?.animal)
            ? normalizeEasternKey(currentUser.birthyear || currentUser.animal)
            : '';
        const calcEastern = uYear ? getEasternSignByYear(uYear)?.key || '' : '';
        const userEasternCandidates = Array.from(new Set([profileEastern, calcEastern].filter(Boolean)));

        const reasons: Array<{
            category: 'zodiac' | 'eastern' | 'number';
            title: string;
            pairLabel: string;
            reasonText: string;
        }> = [];

        // 1. Западная астрология (знаки зодиака)
        if (pZodiac && zodiacPairs) {
            let text = '';
            let pairLabel = '';
            for (const uZ of userZodiacCandidates) {
                const k1 = `${uZ}_${pZodiac}`;
                const k2 = `${pZodiac}_${uZ}`;
                const match = (zodiacPairs[k1] || zodiacPairs[k2] || '').trim();
                if (match) {
                    text = match;
                    pairLabel = `${getZodiacTitle(uZ)} + ${getZodiacTitle(pZodiac)}`;
                    break;
                }
            }
            if (!text) {
                const selfKey = `${pZodiac}_${pZodiac}`;
                const match = (zodiacPairs[selfKey] || zodiacPairs[pZodiac] || '').trim();
                if (match) {
                    text = match;
                    pairLabel = getZodiacTitle(pZodiac);
                }
            }
            if (text) {
                reasons.push({
                    category: 'zodiac',
                    title: 'Несовместимость по западной астрологии',
                    pairLabel,
                    reasonText: text
                });
            }
        }

        // 2. Восточная астрология (китайский календарь)
        if (pEastern && easternPairs) {
            let text = '';
            let pairLabel = '';
            for (const uE of userEasternCandidates) {
                const k1 = `${uE}_${pEastern}`;
                const k2 = `${pEastern}_${uE}`;
                const match = (easternPairs[k1] || easternPairs[k2] || '').trim();
                if (match) {
                    text = match;
                    pairLabel = `${getEasternTitle(uE)} + ${getEasternTitle(pEastern)}`;
                    break;
                }
            }
            if (!text) {
                const selfKey = `${pEastern}_${pEastern}`;
                const match = (easternPairs[selfKey] || easternPairs[pEastern] || '').trim();
                if (match) {
                    text = match;
                    pairLabel = getEasternTitle(pEastern);
                }
            }
            if (text) {
                reasons.push({
                    category: 'eastern',
                    title: 'Несовместимость по восточной астрологии',
                    pairLabel,
                    reasonText: text
                });
            }
        }

        // 3. По числу рождения (числа дня рождения)
        if (pDay && numberPairs) {
            let text = '';
            let pairLabel = '';
            if (uDay) {
                const k1 = `${uDay}_${pDay}`;
                const k2 = `${pDay}_${uDay}`;
                const match = (numberPairs[k1] || numberPairs[k2] || '').trim();
                if (match) {
                    text = match;
                    pairLabel = `${uDay}-е и ${pDay}-е числа`;
                }
            }
            if (!text) {
                const selfKey = `${pDay}_${pDay}`;
                const match = (numberPairs[selfKey] || numberPairs[String(pDay)] || '').trim();
                if (match) {
                    text = match;
                    pairLabel = `${pDay}-е число`;
                }
            }
            if (text) {
                reasons.push({
                    category: 'number',
                    title: 'Несовместимость по числу рождения',
                    pairLabel,
                    reasonText: text
                });
            }
        }

        setMatchedReasons(reasons);

        // Запускаем 1-секундную анимацию лоадера перед отображением вердикта
        setIsChecking(true);
        setTimeout(() => {
            setIsChecking(false);
            setCheckResult(true);
        }, 1000);
    };

    const handleDateChange = (val: Dayjs | null) => {
        setPartnerDate(val);
        if (validationError) {
            setValidationError('');
        }
        if (checkResult) {
            setCheckResult(false);
            setMatchedReasons([]);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    m: { xs: 1.5, sm: 3 },
                    width: { xs: 'calc(100% - 24px)', sm: 'auto' },
                    maxWidth: { xs: '440px', sm: '560px' },
                    borderRadius: { xs: 2.5, sm: 3 },
                    p: { xs: 0.5, sm: 1.5 },
                    background: 'linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)',
                    boxShadow: '0 12px 36px rgba(25, 118, 210, 0.15)',
                }
            }}
        >
            <Box sx={{display: 'flex', justifyContent: 'flex-end', position: 'relative'}}>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: 'absolute',
                        right: { xs: 2, sm: 0 },
                        top: { xs: 2, sm: 0 },
                        color: (theme) => theme.palette.grey[500],
                        p: { xs: 0.5, sm: 1 }
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogTitle sx={{textAlign: 'center', pt: { xs: 1.2, sm: 1 }, pb: { xs: 0.5, sm: 1 }, px: { xs: 1.5, sm: 2 }}}>
                <Typography variant="h6" component="div" sx={{fontWeight: 700, color: '#1a237e', lineHeight: 1.25, fontSize: { xs: '1.05rem', sm: '1.25rem' }}}>
                    Здравствуйте!
                </Typography>
                <Typography variant="body1" sx={{color: '#455a64', mt: { xs: 0.5, sm: 1 }, px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.82rem', sm: '0.95rem' }, lineHeight: { xs: 1.35, sm: 1.45 }}}>
                    {introText}
                </Typography>
            </DialogTitle>

            <DialogContent sx={{pt: { xs: 0.5, sm: 1 }, px: { xs: 1.5, sm: 3 }, pb: { xs: 0.5, sm: 1 }}}>
                {/* Плашка с количеством подобраных людей */}
                <Box
                    sx={{
                        p: { xs: 1, sm: 1.8 },
                        mb: { xs: 1.5, sm: 2.5 },
                        backgroundColor: '#eef5fc',
                        borderRadius: { xs: 2, sm: 2.5 },
                        border: '1px solid #c9e0fa'
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: { xs: 0.8, sm: 1.5 }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <PeopleAltIcon sx={{ color: '#1976d2', fontSize: { xs: 20, sm: 24 } }} />
                            <Typography variant="body1" sx={{ fontWeight: 700, color: '#37474f', fontSize: { xs: '0.84rem', sm: '0.95rem' } }}>
                                Их количество составило:
                            </Typography>
                        </Box>

                        {isLoading ? (
                            <CircularProgress size={20} sx={{ mx: 0.5 }} />
                        ) : (
                            <Typography
                                variant="h4"
                                component="span"
                                sx={{
                                    fontWeight: 800,
                                    color: '#1565c0',
                                    fontSize: { xs: '1.25rem', sm: '1.8rem' },
                                    lineHeight: 1,
                                    px: { xs: 0.8, sm: 1.2 },
                                    py: { xs: 0.2, sm: 0.3 },
                                    bgcolor: '#ffffff',
                                    borderRadius: 1.5,
                                    border: '1px solid #bbdefb',
                                    boxShadow: '0 1px 4px rgba(25,118,210,0.1)'
                                }}
                            >
                                {totalCount}
                            </Typography>
                        )}

                        <Typography variant="body2" sx={{ color: '#546e7a', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                            (Основная база: <b>{mainCount}</b> • Подписчики ВК: <b>{vkCount}</b>)
                        </Typography>
                    </Box>
                </Box>

                {/* Блок экспресс-проверки / результатов проверки */}
                {!checkResult ? (
                    <Box
                        sx={{
                            p: { xs: 1.5, sm: 2.2 },
                            backgroundColor: '#ffffff',
                            borderRadius: { xs: 2, sm: 2.5 },
                            border: '1px solid #e0e0e0',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: { xs: 0.5, sm: 1 } }}>
                            <FavoriteIcon sx={{ color: '#e91e63', fontSize: { xs: 18, sm: 22 } }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#263238', fontSize: { xs: '0.88rem', sm: '1rem' } }}>
                                {checkTitleText}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#546e7a', mb: { xs: 1.2, sm: 2 }, fontSize: { xs: '0.76rem', sm: '0.875rem' }, lineHeight: { xs: 1.35, sm: 1.43 } }}>
                            {checkSubtitleText}
                        </Typography>

                        <LocalizationProvider
                            adapterLocale="ru"
                            dateAdapter={AdapterDayjs}
                            localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
                        >
                            <Box component="form" onSubmit={handleCheckCompatibility}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 1.5 }} alignItems="stretch">
                                    <DatePicker
                                        label="Дата рождения партнера"
                                        format="DD.MM.YYYY"
                                        value={partnerDate}
                                        onChange={handleDateChange}
                                        minDate={dayjs('1900-01-01')}
                                        maxDate={dayjs().subtract(18, 'year')}
                                        sx={{
                                            flex: 1,
                                            width: { xs: '100%', sm: 'auto' },
                                        }}
                                        slotProps={{
                                            inputAdornment: {
                                                position: 'end'
                                            },
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                error: !!validationError,
                                                helperText: validationError,
                                                placeholder: 'ДД.ММ.ГГГГ',
                                                sx: {
                                                    '& .MuiInputBase-input': {
                                                        fontSize: { xs: '0.82rem', sm: '0.92rem' },
                                                        py: { xs: 0.7, sm: 1 }
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        fontSize: { xs: '0.8rem', sm: '0.88rem' }
                                                    }
                                                }
                                            },
                                            popper: {
                                                sx: { zIndex: 1500 }
                                            },
                                            dialog: {
                                                sx: { zIndex: 1500 }
                                            },
                                            day: {
                                                sx: {
                                                    '&:hover': {
                                                        backgroundColor: '#02D2D2',
                                                        transition: 'background-color 0.3s ease',
                                                        color: '#fff'
                                                    },
                                                    '&.MuiButtonBase-root.MuiPickersDay-root.Mui-selected': {
                                                        backgroundColor: '#05B5CD'
                                                    },
                                                    '&.Mui-selected': {
                                                        color: '#fff',
                                                        backgroundColor: '#05B5CD',
                                                        '&:hover': {
                                                            color: '#fff',
                                                            backgroundColor: '#05B5CD',
                                                            transition: 'background-color 0.3s ease',
                                                        },
                                                        '&:focus': {
                                                            color: '#fff',
                                                            backgroundColor: '#05B5CD',
                                                            transition: 'background-color 0.3s ease',
                                                        },
                                                    },
                                                    '&.Mui-selected:focus': {
                                                        backgroundColor: '#05B5CD',
                                                        color: '#fff'
                                                    },
                                                    '&.Mui-selected:hover': {
                                                        backgroundColor: '#05B5CD',
                                                        color: '#fff'
                                                    },
                                                    '.MuiPickersMonth-monthButton.Mui-selected': {
                                                        color: '#fff',
                                                        backgroundColor: '#05B5CD',
                                                    },
                                                    '.MuiPickersYear-yearButton.Mui-selected': {
                                                        color: '#fff',
                                                        backgroundColor: '#05B5CD',
                                                    },
                                                    '.MuiPickersYear-root > .MuiPickersYear-yearButton:focus': {
                                                        backgroundColor: '#02D2D2',
                                                        color: '#fff'
                                                    },
                                                    '.MuiPickersYear-root > .MuiPickersYear-yearButton:hover': {
                                                        backgroundColor: '#02D2D2',
                                                        color: '#fff'
                                                    },
                                                    '.MuiPickersYear-root > .MuiPickersYear-yearButton.Mui-selected:focus': {
                                                        backgroundColor: '#05B5CD'
                                                    },
                                                    '.MuiPickersYear-root > .MuiPickersYear-yearButton.Mui-selected:hover': {
                                                        backgroundColor: '#05B5CD'
                                                    },
                                                }
                                            },
                                            desktopPaper: {
                                                sx: {
                                                    '.MuiPickersMonth-monthButton.Mui-selected': {
                                                        color: '#fff',
                                                        backgroundColor: '#05B5CD',
                                                    },
                                                    '.MuiPickersYear-yearButton.Mui-selected': {
                                                        color: '#fff',
                                                        backgroundColor: '#05B5CD',
                                                    },
                                                    '.MuiPickersYear-root > .MuiPickersYear-yearButton:focus': {
                                                        backgroundColor: '#02D2D2',
                                                        color: '#fff'
                                                    },
                                                    '.MuiPickersYear-root > .MuiPickersYear-yearButton:hover': {
                                                        backgroundColor: '#02D2D2',
                                                        color: '#fff'
                                                    },
                                                    '.MuiPickersYear-root > .MuiPickersYear-yearButton.Mui-selected:focus': {
                                                        backgroundColor: '#05B5CD'
                                                    },
                                                    '.MuiPickersYear-root > .MuiPickersYear-yearButton.Mui-selected:hover': {
                                                        backgroundColor: '#05B5CD'
                                                    },
                                                },
                                            },
                                            mobilePaper: {
                                                sx: {
                                                    '.MuiPickersMonth-monthButton.Mui-selected': {
                                                        color: '#fff',
                                                        backgroundColor: '#05B5CD',
                                                    },
                                                    '.MuiPickersYear-yearButton.Mui-selected': {
                                                        color: '#fff',
                                                        backgroundColor: '#05B5CD',
                                                    },
                                                    '.MuiPickersYear-root > .MuiPickersYear-yearButton:focus': {
                                                        backgroundColor: '#02D2D2',
                                                        color: '#fff'
                                                    },
                                                    '.MuiPickersYear-root > .MuiPickersYear-yearButton:hover': {
                                                        backgroundColor: '#02D2D2',
                                                        color: '#fff'
                                                    },
                                                    '.MuiPickersYear-root > .MuiPickersYear-yearButton.Mui-selected:focus': {
                                                        backgroundColor: '#05B5CD'
                                                    },
                                                    '.MuiPickersYear-yearButton.Mui-selected:hover': {
                                                        backgroundColor: '#05B5CD'
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isChecking}
                                        sx={{
                                            height: { xs: 36, sm: 40 },
                                            px: { xs: 2, sm: 3 },
                                            whiteSpace: 'nowrap',
                                            width: { xs: '100%', sm: 'auto' },
                                            fontWeight: 600,
                                            fontSize: { xs: '0.82rem', sm: '0.875rem' },
                                            boxShadow: 'none'
                                        }}
                                    >
                                        {isChecking ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CircularProgress size={16} color="inherit" />
                                                <span>Проверка...</span>
                                            </Box>
                                        ) : (
                                            'Проверить'
                                        )}
                                    </Button>
                                </Stack>
                            </Box>
                        </LocalizationProvider>

                        {/* Индикатор загрузки проверки */}
                        {isChecking && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.2, py: { xs: 1.5, sm: 2.5 } }}>
                                <CircularProgress size={20} sx={{ color: '#1976d2' }} />
                                <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                    Проверка совместимости...
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ width: '100%', pt: { xs: 0.5, sm: 1 } }}>
                        {/* Единая плашка результатов с общей фразой и всеми причинами с новой строки */}
                        <Box
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                borderRadius: 2.5,
                                backgroundColor: '#fff8f6',
                                border: '1px solid #ffcdd2',
                                borderLeft: '5px solid #d32f2f',
                                boxShadow: '0 2px 8px rgba(211, 47, 47, 0.05)',
                                mb: 1.5
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 800,
                                    color: '#c62828',
                                    fontSize: { xs: '0.92rem', sm: '0.92rem' },
                                    mb: matchedReasons.length > 0 ? 1.2 : 0
                                }}
                            >
                                {incompatibleText}
                            </Typography>

                            {matchedReasons.length > 0 && (
                                <Stack spacing={0.8}>
                                    {matchedReasons.map((item, idx) => {
                                        const rawText = item.reasonText.trim();
                                        const hasPunctuation = rawText.endsWith(';') || rawText.endsWith('.');
                                        const formattedReasonText = hasPunctuation 
                                            ? rawText 
                                            : (idx < matchedReasons.length - 1 ? `${rawText};` : `${rawText}.`);

                                        return (
                                            <Typography
                                                key={idx}
                                                variant="body2"
                                                sx={{
                                                    color: '#263238',
                                                    fontSize: { xs: '0.84rem', sm: '0.92rem' },
                                                    lineHeight: 1.5,
                                                    whiteSpace: 'pre-line'
                                                }}
                                            >
                                                <Box component="span" sx={{ fontWeight: 700, color: '#1a237e' }}>
                                                    {item.title}:
                                                </Box>{' '}
                                                {formattedReasonText}
                                            </Typography>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>

                        <Typography variant="body2" sx={{ mb: 1.2, color: '#455a64', fontSize: { xs: '0.78rem', sm: '0.875rem' }, lineHeight: { xs: 1.35, sm: 1.45 } }}>
                            {astrologerText}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                            <Button
                                variant="outlined"
                                size="small"
                                href="https://vk.ru/goroskoper2000"
                                target="_blank"
                                rel="nofollow noreferrer"
                                startIcon={
                                    <Box
                                        component="img"
                                        src="/images/social/vk.svg"
                                        alt="VK"
                                        sx={{ width: { xs: 16, sm: 18 }, height: { xs: 16, sm: 18 }, borderRadius: '4px', display: 'block' }}
                                    />
                                }
                                sx={{
                                    py: { xs: 0.35, sm: 0.5 },
                                    px: { xs: 1.2, sm: 1.8 },
                                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                    borderColor: '#0077ff',
                                    color: '#0077ff',
                                    fontWeight: 600,
                                    '&:hover': {
                                        borderColor: '#005bbf',
                                        backgroundColor: 'rgba(0, 119, 255, 0.05)'
                                    }
                                }}
                            >
                                <Box component="span" sx={{ position: 'relative', top: '1px' }}>VK</Box>
                            </Button>

                            <Button
                                size="small"
                                onClick={() => setCheckResult(false)}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                    color: '#546e7a',
                                    fontWeight: 600
                                }}
                            >
                                Проверить другую дату
                            </Button>
                        </Stack>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{px: {xs: 1.5, sm: 3}, pb: {xs: 1.5, sm: 2.5}, pt: {xs: 0.5, sm: 1}}}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    fullWidth
                    size="medium"
                    sx={{
                        py: { xs: 0.9, sm: 1.3 },
                        fontWeight: 700,
                        fontSize: { xs: '0.85rem', sm: '1rem' },
                        textTransform: 'none',
                        borderRadius: 2,
                        backgroundColor: '#1976d2',
                        boxShadow: '0 4px 14px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                            backgroundColor: '#1565c0',
                            boxShadow: '0 6px 18px rgba(25, 118, 210, 0.4)',
                        }
                    }}
                >
                    Перейти к просмотру анкет
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MatchBannerDialog;
